// @flow
import React from 'react'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import Loading from '@/components/common/loading'
import { PUBLICITY_PUBLIC } from '@/utils/constants'
import ErrorPage from '@/views//error'
import AlbumContainer from '@/views/media/album'
import Head from '@components/common/head'
import { getAlbum } from '@data/api/album'

interface Props {
  id: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultAlbum?: any
}

const swrOptions = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
}

async function fetcher(id: string) {
  const result = await getAlbum(id)
  if (result.error) {
    return Promise.reject(result.error)
  }
  return result
}

const Album = ({ id, defaultAlbum }: Props) => {
  const router = useRouter()
  const { data: album, isLoading, error } = useSWR(
    id ? `/api/album/${id}` : null,
    () => fetcher(id),
    defaultAlbum ? { fallbackData: defaultAlbum } : swrOptions,
  )

  const buy = !!router.query.buy

  if (!album && (isLoading || !router.query.p)) {
    return (
      <Loading />
    )
  }

  if (!album || error) {
    return (
      <ErrorPage statusCode={404} />
    )
  }

  return (
    <>
      <Head
        title={`${album.title} by ${album.user.name}`}
        description={album.description}
        url={`/album/${album.id}/${album.slug}`}
        image={album.coverart_crop_url}
        user={album.user.social ? album.user.social.twitter : ''}
        isPrivate={album.status !== PUBLICITY_PUBLIC}
      />
      <AlbumContainer album={album} buy={buy} />
    </>
  )
}

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = async ({
  params,
}) => {
  const [ id ] = params.p

  if (!id) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  // fetch sound data here
  const album = await getAlbum(id)

  // check if user is not exists after fetch
  if (album.error) {
    return {
      props: { id },
      revalidate: 60,
    }
  }

  return {
    props: {
      id,
      defaultAlbum: {
        ...album,
        id,
      },
      breadcrumbs: [
        {
          label: album.user.name,
          to: `/user/${album.user.username}`,
        },
        { label: album.title },
      ],
    },
    revalidate: 60,
  }
}

export default Album
